// Seeds the review step's workpaper decisions: every selected work step with
// no decision yet gets its Optro candidates fetched and written into the
// wizard as an EXPLICIT pick of all of them, so the row reads exactly like
// one the user chose by hand and survives moving between wizard steps. Rows
// that already hold a decision — a manual pick, an explicit empty set, or an
// earlier seed — are left alone.
//
// Fetches run at most `MAX_IN_FLIGHT` at a time. Each decision written re-runs
// the effect, which is what starts the next waiting row; the in-flight set
// lives in a ref so those re-runs never cancel or duplicate a fetch. Results
// arriving after the page unmounts are dropped.
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchOptroWorkStepWorkpapers } from "../../../../hooks/useOptroWorkStepWorkpapers";
import {
  OptroWorkStepSummary,
  WorkStepWorkpaperDecision,
} from "../../../../models/audit/workStep";

const MAX_IN_FLIGHT = 4;

export function useSeedWorkStepWorkpapers(
  selected: OptroWorkStepSummary[],
  workpaperDecisions: Record<string, WorkStepWorkpaperDecision>,
  setWorkpapers: (
    externalRef: string,
    decision: WorkStepWorkpaperDecision,
  ) => void,
): {
  // True while a row's candidates are still queued or in flight. A row
  // whose fetch failed is not pending: it keeps no decision and imports
  // with the wire default (every Optro file, resolved by the backend).
  isPending: (externalRef: string) => boolean;
} {
  const inFlight = useRef(new Set<string>());
  const [failed, setFailed] = useState<ReadonlySet<string>>(new Set());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const waiting = selected.filter(
      (workStep) =>
        !(workStep.externalRef in workpaperDecisions) &&
        !failed.has(workStep.externalRef) &&
        !inFlight.current.has(workStep.externalRef),
    );
    const slots = Math.max(0, MAX_IN_FLIGHT - inFlight.current.size);
    waiting.slice(0, slots).forEach((workStep) => {
      const ref = workStep.externalRef;
      inFlight.current.add(ref);
      fetchOptroWorkStepWorkpapers(workStep.opsAuditItemId).then(
        (files) => {
          inFlight.current.delete(ref);
          if (mounted.current) setWorkpapers(ref, { mode: "explicit", files });
        },
        (error: unknown) => {
          console.error(`Failed to load workpapers for ${ref}:`, error);
          inFlight.current.delete(ref);
          if (mounted.current) setFailed((prev) => new Set(prev).add(ref));
        },
      );
    });
  }, [selected, workpaperDecisions, failed, setWorkpapers]);

  const isPending = useCallback(
    (externalRef: string) =>
      !(externalRef in workpaperDecisions) && !failed.has(externalRef),
    [workpaperDecisions, failed],
  );

  return { isPending };
}
